/**
 * Veřejné čtení právních dokumentů.
 *
 * ``GET /api/v1/legal/{slug}`` vrací dokument **toho, kdo web
 * provozuje** — na apexu platformy její vlastní texty, na shopu
 * tenanta jeho obchodní podmínky (nebo platformní šablonu s doplněnými
 * údaji jeho firmy). Shell rozhoduje server, FE jen řekne slug.
 *
 * Endpoint vrací ``null`` (200), ne 404, když dokument neexistuje.
 * Volající pak spadne zpět na zkompilovaný i18n text, který stránky
 * renderovaly dosud — během plnění obsahu (fáze F2 specu) je to
 * zamýšlený stav, ne chyba, a 404 by se v konzoli tvářilo jako závada.
 */

import { useQuery } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";

const API = "/api/v1";

export interface PublicLegalDocument {
  kind_key: string;
  slug: string;
  /** Jazyk VYDANÉHO textu — může se lišit od dotazu, viz níž. */
  lang: string;
  version: string;
  title: string;
  body: string;
  published_at: string | null;
  /**
   * Dokument v požadovaném jazyce publikovaný není a server vydal jiný
   * (KAN-403). Prázdné podmínky jsou horší než podmínky v cizím jazyce —
   * ale zamlčený cizí jazyk je horší než přiznaný, takže to stránka
   * musí říct.
   */
  is_lang_fallback: boolean;
}

export interface PublicLegalLink {
  kind_key: string;
  slug: string;
  title: string;
  /** Verze publikovaného znění — cookies lišta ji zapisuje do ledgeru. */
  version: string;
}

/**
 * Publikované dokumenty tohohle shellu — zdroj odkazů v patičce.
 *
 * Vrací jen to, co se skutečně resolvuje, takže patička nikdy nenabídne
 * odkaz končící prázdnou stránkou. Zároveň se tím sama udrží aktuální:
 * nový druh dokumentu se v patičce objeví, jakmile je publikovaný, bez
 * sahání do FE.
 */
export function usePublicLegalIndex(lang: string) {
  return useQuery<{ items: PublicLegalLink[] }>({
    queryKey: ["legal", "index", lang],
    queryFn: () =>
      apiFetch<{ items: PublicLegalLink[] }>(
        `${API}/legal?lang=${encodeURIComponent(lang)}`,
      ),
    staleTime: 10 * 60 * 1000,
  });
}

/**
 * ``version`` vydá konkrétní starší znění místo aktuálního — čte to
 * potvrzení objednávky, aby se zákazník dostal k tomu, co skutečně
 * přijal. Server vydává jen **publikované** verze a jen v rámci scope
 * daného shellu, takže se přes ten parametr nedá dostat na koncept ani
 * na znění jiného tenanta.
 */
export function usePublicLegalDocument(
  slug: string | null,
  lang: string,
  version?: string | null,
) {
  return useQuery<PublicLegalDocument | null>({
    queryKey: ["legal", "document", slug, lang, version ?? null],
    queryFn: () =>
      apiFetch<PublicLegalDocument | null>(
        `${API}/legal/${slug}?lang=${encodeURIComponent(lang)}` +
          (version ? `&version=${encodeURIComponent(version)}` : ""),
      ),
    enabled: slug !== null,
    // Odpovídá Cache-Control hlavičce na BE (10 minut). Právní text se
    // nemění po sekundách.
    staleTime: 10 * 60 * 1000,
  });
}

export interface LegalUpdate {
  kind_key: string;
  slug: string;
  title: string;
  accepted_version: string;
  current_version: string;
}

/**
 * Dokumenty, jejichž znění se od zákazníkova souhlasu změnilo.
 *
 * Server je odvozuje **při čtení** porovnáním „co jsi přijal" s tím, co
 * platí — žádná tabulka nepřečtených oznámení, která by se s realitou
 * rozešla. Banner proto po novém souhlasu zmizí sám.
 */
export function useLegalUpdates(enabled: boolean) {
  return useQuery<{ items: LegalUpdate[] }>({
    queryKey: ["legal", "updates"],
    queryFn: () => apiFetch<{ items: LegalUpdate[] }>(`${API}/account/legal-updates`),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}
